//#region IMPORT
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
/** Services Import */
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
/** Components */
import useDelete from '../../../../hooks/useDeleteN';
import useActionLog from '../../../../hooks/useActionLog';
/** Icons Import */
import SAF_BASIC_02 from './SAF_BASIC_02';
import {
  useSearch1,
  useSearch2,
  useSearch3,
  useSave2,
  useDelete1,
} from './SAF_BASIC_02_QUERY';
//#endregion

// 화면 MENU_ID
const SCREEN_MENU_ID = 'SafeWorkProcedure';

function SAF_BASIC_02F(props) {
  //#region 변수
  const dispatch = useDispatch();
  const { userInfo } = useSelector(
    (state) => state.BaseInfo
  );

  //로그 관련
  const { SaveActionLog } = useActionLog();

  const gridRef1 = useRef(); // 작업계획서
  const gridRef4 = useRef(); // 반려이력 그리드

  const [rowData1, setRowData1] = useState([]); //그리드 데이터
  const [selectedRowData1, setSelectedRowData1] = useState([]);
  const [changedRowData1, setChangedRowData1] = useState([]);
  const [deletedRowData1, setDeletedRowData1] = useState([]);
  const [clickRowId, setClickRowId] = useState(null);
  const selectedRowNosRef1 = useRef(new Set());
  const lastCheckedIndexRef1 = useRef(null);

  const [modalToggle1, setModalToggle1] = useState(false); // 반려사유팝업
  const [rejectReason, setRejectReason] = useState(''); // 반려사유

  const [modalToggle2, setModalToggle2] = useState(false); // 서명팝업
  const [signInputs, setSignInputs] = useState({
    mapUrl: '',
    mapFiles: {},
  }); // 서명 state
  const signatureCanvas = useRef(); // 서명 REF

  const [modalToggle3, setModalToggle3] = useState(false); // 반려이력팝업
  const [modalRowData3, setModalRowData3] = useState([]); // 반려이력팝업그리드

  // QUERY: 조회/저장/삭제 훅
  const searchInfo = useSelector((state) => state.SearchInfo);

  // context를 useMemo로 동적으로 계산
  // ref는 dependency 배열에 포함하지 않음 (ref.current의 값들을 직접 dependency로 사용)
  const searchRefCurrent = props.searchRef?.current;
  const context = useMemo(
    () => ({
      search: {
        custCd:
          searchInfo.custCd ||
          searchRefCurrent?.custCd ||
          '',
        depCd:
          searchInfo.depCd ||
          searchRefCurrent?.depCd ||
          '',
        workStartDate:
          searchRefCurrent?.startDate ||
          '',
        workEndDate:
          searchRefCurrent?.endDate ||
          '',
        gubun:
          searchInfo.gubun ||
          searchRefCurrent?.gubun ||
          '',
        searchText:
          searchInfo.searchText ||
          searchRefCurrent?.searchText ||
          '',
      },
      userInfo,
    }),
    [
      searchInfo.custCd,
      searchInfo.depCd,
      searchInfo.gubun,
      searchInfo.searchText,
      searchRefCurrent?.custCd,
      searchRefCurrent?.depCd,
      searchRefCurrent?.startDate,
      searchRefCurrent?.endDate,
      searchRefCurrent?.gubun,
      searchRefCurrent?.searchText,
      userInfo,
    ]
  );

  // 삭제
  const delete1 = useDelete1();
  const deleteHandler1 = useDelete({
    gridRef: gridRef1,
    deleteMutation: delete1,
    updateDeleted: setDeletedRowData1,
    updateRowData: setRowData1,
    deletedRowData: deletedRowData1,
    rowData: rowData1,
    setChangedRowData: setChangedRowData1,
  });

  //#endregion

  //#region useEffect
  // 처리상태(gubun) 변경 추적을 위한 ref
  const prevGubunRef = React.useRef('');
  const isInitialMount = React.useRef(true);
  
  // searchClick2 처리 및 gubun 변경 감지
  useEffect(() => {
    const currentGubun = searchInfo.gubun || props.searchRef?.current?.gubun || '';
    
    // 초기 마운트 시 prevGubunRef 초기화
    if (isInitialMount.current) {
      prevGubunRef.current = currentGubun;
      isInitialMount.current = false;
    }
    
    // searchClick2가 true이거나 gubun이 변경된 경우 조회 실행
    const shouldSearch = props.searchClick2 || (prevGubunRef.current !== currentGubun && !isInitialMount.current);
    
    if (!shouldSearch) return;
    
    // gubun 변경 추적 업데이트
    if (prevGubunRef.current !== currentGubun) {
      prevGubunRef.current = currentGubun;
    }
    
    ResetLogic1();

    const { depCd } = props.searchRef?.current || {};
    if (!depCd) {
      if (props.searchClick2) props.setSearchClick2(false);
      return;
    }

    if (!props.validateSelection()) {
      if (props.searchClick2) props.setSearchClick2(false);
      return;
    }

    SearchLogic1();
    if (props.searchClick2) props.setSearchClick2(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.searchClick2, searchInfo.gubun, searchRefCurrent?.gubun]);

  // 버튼 로직
  useEffect(() => {
    if (props.filterClick2) {
      if (!props.validateSelection()) {
        props.setFilterClick2(false);
        return;
      }

      gridRef1.current.api.setQuickFilter(props.searchRef?.current?.searchText || '');
      props.setFilterClick2(false);
    }
    if (props.saveClick2) {
      SaveLogic1();
      props.setSaveClick2(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterClick2, props.saveClick2]);
  //#endregion

  //#region 조회
  const { refetch: Search1 } = useSearch1({
    context: context,
    onSuccess: (rows) => {
      const formattedData = rows.map((item) => {
        const workStartDate = item.WorkStartDate || item.workStartDate || item.WORK_START_DATE;
        const workEndDate = item.WorkEndDate || item.workEndDate || item.WORK_END_DATE;

        const date1 = workStartDate ? new Date(workStartDate) : null;
        const date2 = workEndDate ? new Date(workEndDate) : null;

        const formattedItem = {
          ...item,
          workStartDate: date1
            ? `${String(date1.getFullYear())}-${String(date1.getMonth() + 1).padStart(2, '0')}-${String(date1.getDate()).padStart(2, '0')} ${String(date1.getHours()).padStart(2, '0')}:${String(date1.getMinutes()).padStart(2, '0')}`
            : '',
          workEndDate: date2
            ? `${String(date2.getFullYear())}-${String(date2.getMonth() + 1).padStart(2, '0')}-${String(date2.getDate()).padStart(2, '0')} ${String(date2.getHours()).padStart(2, '0')}:${String(date2.getMinutes()).padStart(2, '0')}`
            : '',
          // 기존 필드명도 유지 (호환성)
          WORK_START_DATE: date1
            ? `${String(date1.getFullYear())}-${String(date1.getMonth() + 1).padStart(2, '0')}-${String(date1.getDate()).padStart(2, '0')} ${String(date1.getHours()).padStart(2, '0')}:${String(date1.getMinutes()).padStart(2, '0')}`
            : '',
          WORK_END_DATE: date2
            ? `${String(date2.getFullYear())}-${String(date2.getMonth() + 1).padStart(2, '0')}-${String(date2.getDate()).padStart(2, '0')} ${String(date2.getHours()).padStart(2, '0')}:${String(date2.getMinutes()).padStart(2, '0')}`
            : '',
        };

        return formattedItem;
      });

      setRowData1(Commons.InsertNumber(formattedData));
    },
  });

  const { runAsync: Save2Async } = useSave2();

  //안전작업허가서 검색 버튼 이벤트
  const SearchLogic1 = async () => {
    setSignInputs({
      mapUrl: '',
      mapFiles: {},
    });
    ResetLogic1();

    try {
      await Search1();
    } catch (error) {
    }
  };
  //#endregion

  // 반려이력 조회
  const { runAsync: Search3 } = useSearch3({
    onSuccess: async (rows) => {
      if (!rows || rows.length === 0) {
        dispatch(
          showSnackbar({
            message: '반려이력이 없습니다.',
            type: 'info',
          })
        );
        setModalRowData3([]);
        return;
      }
      setModalRowData3(rows);
    },
  });

  // 반려이력 조회
  const SearchReturn = async (_PM_CD) => {
    if (!_PM_CD) return;
    setModalRowData3([]);
    await Search3({
      row: { pmCd: _PM_CD },
    });
  };

  // 상세 조회 및 레포트 생성
  const { runAsync: Search2 } = useSearch2({
    onSuccess: async (rows) => {
      if (!rows || rows.length === 0) {
        dispatch(
          showSnackbar({
            message: '상세 정보를 조회할 수 없습니다.',
            type: 'warning',
          })
        );
        return;
      }

      let currentRowData = clickedRowDataRef.current || selectedRowData1?.[0];
      
      if (!currentRowData) {
        await new Promise(resolve => setTimeout(resolve, 0));
        currentRowData = clickedRowDataRef.current || selectedRowData1?.[0];
      }

      try {
        const reportData = rows[0];

        const workTypesString = reportData.workTypes || '';
        const workTypesArray = workTypesString
          ? workTypesString.split(',').map((code) => code.trim())
          : [];

        const workTypeDetails = rows.map((row) => ({
          workTypeDtl: row.workTypeDtl || '',
          itemCheck: row.itemCheck || '',
        })).filter((item) => item.workTypeDtl);

        // 작업관리부서 사인
        const depSignPath = reportData.depSignPath  || '';
        let cleanBase64_dep = null;
        if (depSignPath) {
          try {
            const imageUrl_dep = Global.file.useUrl + 'files/' + fixUrl(depSignPath);
            const base64Image_dep = await ConvertImageToBase64(imageUrl_dep);
            cleanBase64_dep = base64Image_dep.replace(
              /^data:image\/[a-z]+;base64,/,
              ''
            );
          } catch {
          }
        }

        // 허가관리부서 사인
        const smSignPath = reportData.smSignPath || '';
        let cleanBase64_sm = null;
        if (smSignPath) {
          try {
            const imageUrl_sm = Global.file.useUrl + 'files/' + fixUrl(smSignPath);
            const base64Image_sm = await ConvertImageToBase64(imageUrl_sm);
            cleanBase64_sm = base64Image_sm.replace(
              /^data:image\/[a-z]+;base64,/,
              ''
            );
          } catch {
          }
        }

        // 작업종류 체크박스 데이터 생성
        const TableDataMain = [
          {
            WT_001: workTypesArray.includes('WT_001'),
            WT_002: workTypesArray.includes('WT_002'),
            WT_003: workTypesArray.includes('WT_003'),
            WT_004: workTypesArray.includes('WT_004'),
            WT_005: workTypesArray.includes('WT_005'),
            WT_006: workTypesArray.includes('WT_006'),
          },
        ];

        // 작업별 상세 체크 데이터 생성
        const tableDataSingleRow = {};
        workTypeDetails.forEach((item) => {
          const keyBase = item.workTypeDtl;
          if (keyBase) {
            tableDataSingleRow[`CHECK_CONFIRM_${keyBase}`] = '';
            tableDataSingleRow[`CHECK_NOT_APPLICABLE_${keyBase}`] = '';

            if (item.itemCheck === '0') {
              // 확인
              tableDataSingleRow[`CHECK_CONFIRM_${keyBase}`] = 'true';
            } else if (item.itemCheck === '1') {
              // 해당없음
              tableDataSingleRow[`CHECK_NOT_APPLICABLE_${keyBase}`] = 'true';
            }
          }
        });

        const serializedCheckData = JSON.stringify([tableDataSingleRow]);

        const headerData = {
          ...currentRowData,
          PM_CD: reportData.pmCd || '',
          USER_NM: reportData.userNm || '',
          DEP_NM_LAST: reportData.depNmLast || '',
          POSITION: reportData.userPosition || '',
          CREATE_DATE: reportData.createDate || '',
          CONSTRUCTION_NM: reportData.constructionNm || '',
          WORK_START_DATE: reportData.workStartDate || '',
          WORK_END_DATE: reportData.workEndDate || '',
          WORK_DETAIL: reportData.workDetail || '',
          WORK_PLACE: reportData.workPlace || '',
          WORK_MEMBER_NO: reportData.workMemberNo || '',
          SITE_MANAGER: reportData.siteManager || '',
          SITE_PHONE: reportData.sitePhone || '',
          SM_DEP_NM_LAST: reportData.smDepNmLast || '',
          DEP_HEAD_POSITION: reportData.smPosition || '',
          DEP_HEAD_NM: reportData.smName || '',
          DEP_SIGN_PATH: cleanBase64_dep,
          SM_SIGN_PATH: cleanBase64_sm,
        };

        const printData = {
          FileName: 'PER_BASIC',
          HeaderData: JSON.stringify(headerData),
          TableDataMain,
          TableData1: serializedCheckData,
          TableData2: serializedCheckData,
          TableData3: serializedCheckData,
          TableData4: serializedCheckData,
          TableData5: serializedCheckData,
          TableData6: serializedCheckData,
        };

        const response = await Commons.Axios(
          'report',
          Global.base.useUrl + Global.urls.Reports.reports,
          printData
        );

        if (!response || !response.data) {
          throw new Error('레포트 생성 응답이 없습니다.');
        }

        // Blob URL 생성
        const blobUrl = URL.createObjectURL(response.data);

        // selectedRowData1 업데이트 (previewFilePath 추가)
        const pmCd =  reportData.pmCd || '';
        setSelectedRowData1((prev) => {
          if (prev.length === 0) return prev;
          return [
            {
              ...prev[0],
              saveName: `안전작업허가서_${pmCd}.pdf`,
              filePath: blobUrl, // Blob URL 직접 사용
              previewFilePath: blobUrl,
            },
          ];
        });

      } catch (e) {
        console.error('레포트 생성 오류:', e);
        dispatch(
          showSnackbar({
            message: '레포트 생성 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
      } finally {
      }
    },
  });

  // 클릭한 행 데이터를 저장하기 위한 ref
  const clickedRowDataRef = useRef(null);

  // 행 클릭 시 상세 조회
  const SearchDetailLogic = async (pmCd, rowData = null) => {
    if (!pmCd) return;
    
    // 행 데이터가 전달되면 ref에 저장
    if (rowData) {
      clickedRowDataRef.current = rowData;
    }
    
    await Search2({
      row: { pmCd },
    });
  };

  //#region 저장
  // 삭제 버튼 클릭 시 함수
  const SaveLogic1 = async () => {
    try {
      const hasDelete = deletedRowData1.length > 0;
      if (!hasDelete) {
        dispatch(
          showSnackbar({
            message: '삭제할 항목이 없습니다.',
            type: 'warning',
          })
        );
        return;
      }

      try {
        await deleteHandler1.DeleteLogic();
        
        dispatch(
          showSnackbar({
            message: '삭제되었습니다.',
            type: 'success',
          })
        );

        // 정상 완료 시 로그
        await SaveActionLog([...deletedRowData1], {
          menuId: SCREEN_MENU_ID,
          coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
          userId: userInfo.userid,
          logResult: 'SUCCESS',
        });

        SearchLogic1(); // 다시 조회
      } catch (e) {
        // 오류 시 로그
        await SaveActionLog([...deletedRowData1], {
          menuId: SCREEN_MENU_ID,
          coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
          userId: userInfo.userid,
          logResult: 'ERROR',
          errorMsg: e.message,
        });

        dispatch(
          showSnackbar({
            message: e.message || '삭제 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
      }
    } catch (e) {
      console.error(e);
      dispatch(
        showSnackbar({
          message: e.message || '오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  // 반려 처리 함수
  const RejectLogic = async (reason) => {
    try {
      // 선택된 행이 없으면 오류
      if (!selectedRowData1 || selectedRowData1.length === 0) {
        dispatch(
          showSnackbar({
            message: '반려할 항목을 선택해주세요.',
            type: 'warning',
          })
        );
        return;
      }

      const context = {
        userInfo: userInfo,
      };

      // 반려 저장할 데이터 준비
      const saveRows = selectedRowData1.map((item) => {
        return {
          pmCd: item.PM_CD || item.pmCd || '',
          gubun: '0', // 반려
          returnDetail: reason || '',
          depSignPath: '',
          smSignPath: '',
          depHeadId: '',
        };
      });

      // Save2Async 호출 (반려 API)
      const result = await Save2Async(saveRows, context);

      const responseData = result?.data || result;
      
      if (responseData) {
        const results = Array.isArray(responseData) ? responseData : [responseData];
        const errorResult = results.find((r) => !r.isSuccess || r.type === 'ERROR');
        
        if (errorResult) {
          dispatch(
            showSnackbar({
              message: errorResult.message || errorResult.errorMsg || '반려 처리 중 오류가 발생했습니다.',
              type: 'error',
            })
          );
          return;
        }
      }

      dispatch(
        showSnackbar({
          message: '반려 되었습니다.',
          type: 'success',
        })
      );

      // 정상 완료 시 로그
      await SaveActionLog([...selectedRowData1], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'SUCCESS',
      });

      await SignRequest('0');
      SearchLogic1(); // 다시 조회
    } catch (e) {
      // 오류 시 로그
      await SaveActionLog([...selectedRowData1], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'ERROR',
        errorMsg: e.message,
      });

      console.error(e);
      dispatch(
        showSnackbar({
          message: e.message || '오류가 발생했습니다.',
          type: e.code === 'CUSTOM_ERROR' ? e.type : 'error',
        })
      );
    }
  };

  // 승인 처리 함수
  const ApproveLogic = async (gubun, sign) => {
    try {
      // 선택된 행이 없으면 오류
      if (!selectedRowData1 || selectedRowData1.length === 0) {
        dispatch(
          showSnackbar({
            message: '승인할 항목을 선택해주세요.',
            type: 'warning',
          })
        );
        return;
      }

      const context = {
        userInfo: userInfo,
      };

      // 서명 파일 저장 (안전관리자 승인 시)
      if (gubun === '2' && signInputs.mapFiles.size) {
        await SaveLogic3(signInputs.mapFiles);
      }

      // 승인 저장할 데이터 준비
      const saveRows = selectedRowData1.map((item) => {
        const smSignPath =
          gubun === '2' && signInputs.mapFiles.name
            ? `Business/${props.searchRef?.current?.custCd || ''}/SAF_BASIC_02/${Commons.DecryptoAES(userInfo.userid)}/${signInputs.mapFiles.name}`
            : '';

        return {
          pmCd: item.pmCd || '',
          gubun: gubun, // '1': 작업관리부서 승인, '2': 허가관리부서 승인
          returnDetail: '',
          depSignPath: gubun === '1' ? '' : '', // 작업관리부서 승인 시 서명 경로
          smSignPath: smSignPath,
          depHeadId: gubun === '1' ? userInfo.userid || '' : '',
        };
      });

      // Save2Async 호출 (승인 API)
      const result = await Save2Async(saveRows, context);

      const responseData = result?.data || result;
      
      if (responseData) {
        const results = Array.isArray(responseData) ? responseData : [responseData];
        const errorResult = results.find((r) => !r.isSuccess || r.type === 'ERROR');
        
        if (errorResult) {
          dispatch(
            showSnackbar({
              message: errorResult.message || errorResult.errorMsg || '승인 처리 중 오류가 발생했습니다.',
              type: 'error',
            })
          );
          return;
        }
      }

      // 결과 처리
      if (gubun === '1') {
        dispatch(
          showSnackbar({
            message: '작업관리부서 승인 되었습니다.',
            type: 'success',
          })
        );
      } else if (gubun === '2') {
        dispatch(
          showSnackbar({
            message: '허가관리부서 승인 되었습니다.',
            type: 'success',
          })
        );
      }

      // 정상 완료 시 로그
      await SaveActionLog([...selectedRowData1], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'SUCCESS',
      });

      await SignRequest(gubun);
      SearchLogic1(); // 다시 조회
    } catch (e) {
      // 오류 시 로그
      await SaveActionLog([...selectedRowData1], {
        menuId: SCREEN_MENU_ID,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'ERROR',
        errorMsg: e.message,
      });

      console.error(e);
      dispatch(
        showSnackbar({
          message: e.message || '오류가 발생했습니다.',
          type: e.code === 'CUSTOM_ERROR' ? e.type : 'error',
        })
      );
    }
  };

  // 서명 저장
  const SaveLogic3 = async (_value1) => {
    try {
      let formList = new FormData();
      let filePath = `Business/${props.searchRef.current.custCd}/SAF_BASIC_02/${Commons.DecryptoAES(userInfo.userid)}`;

      formList.append('UserId', userInfo.userid);
      formList.append('FilePath', filePath); // 저장경로(파일명 제외)
      formList.append('FormFile', _value1); // 파일

      const result = await Commons.Axios(
        'file',
        Global.file.useUrl + Global.urls.Execute.videoUpload,
        null,
        formList
      );
      const data = result.data;

      if (data?.filePath) {
      } else {
        dispatch(
          showSnackbar({
            message: '파일 업로드 실패 또는 파일 경로 없음',
            type: 'error',
          })
        );
        return null;
      }
    } catch (e) {
    } finally {
    }
  };

  //#endregion

  //#region 나머지 함수

  const NewYnErrorMsg = () => {
    dispatch(
      showSnackbar({
        message: '데이터 입력중입니다. 저장 후 시도하세요.',
        type: 'warning',
      })
    );
  };
  const handleRowSelection1 = (rowData, rowIndex) => {
    // 이전에 클릭한 행(seq 기준)을 기억
    if (clickRowId === rowData.no) return; // 같은 행이면 무시

    setClickRowId(rowData.no); // 현재 클릭한 행 ID 저장

    // 행 클릭 시 selectedRowData1을 먼저 설정 (새로고침 후나 처음 렌더링 시 onSelectionChanged가 아직 실행되지 않았을 수 있음)
    setSelectedRowData1([rowData]);

    // 행 클릭 시 상세 조회 (행 데이터를 직접 전달하여 ref에 저장)
    const pmCd = rowData.pmCd || rowData.PM_CD;
    if (pmCd) {
      SearchDetailLogic(pmCd, rowData);
    }
  };
  const ResetLogic1 = () => {
    setRowData1([]);
    setSelectedRowData1([]);
    setChangedRowData1([]);
    setDeletedRowData1([]);
  };

  // 서명 이미지 파싱(url => base64)
  const ConvertImageToBase64 = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result); // Base64 문자열 반환
      };
      reader.onerror = reject; // 오류 처리
      reader.readAsDataURL(blob); // Blob을 Base64로 변환
    });
  };

  const fixUrl = (url) => {
    if (!url || typeof url !== 'string') return ''; // 또는 적절한 fallback
    const parts = url.split('/');
    const fileName = parts.pop(); // 파일 이름 분리
    const encodedFileName = encodeURIComponent(fileName);
    return [...parts, encodedFileName].join('/');
  };

  // 변경사유 팝업
  const PopupClickEvent1 = async () => {
    setModalToggle1(true);
    setRejectReason('');
  };

  // 서명 팝업
  const PopupClickEvent2 = async () => {
    setModalToggle2(true);
    setSignInputs({
      mapUrl: '',
      mapFiles: {},
    });
  };

  // 변경이력 조회 팝업
  const PopupClickEvent3 = async (_PM_CD) => {
    setModalToggle3(true);
    await SearchReturn(_PM_CD);
  };

  const SignRequest = async (_GUBUN) => {
    try {
      const item = selectedRowData1[0];
      if (!item) {
        return;
      }

      let saveData = [];

      if (_GUBUN === '0') {
        selectedRowData1.forEach((item) => {
          const row = {
            responseId: Commons.EncryptAES(item.USER_ID),
            title: Commons.EncryptAES('작업허가서'),
            body: Commons.EncryptAES('작업허가서 반려 안내'),
            gubun: Commons.EncryptAES('AL_002_002'),
            fcmToken: Commons.EncryptAES(''),
            requestId: userInfo.userid,
            route: Commons.EncryptAES(`/M_Permit`),
            responseData: Commons.EncryptAES(item.PM_CD),
          };
          saveData.push(row);
        });
      } else if (_GUBUN === '2') {
        selectedRowData1.forEach((item) => {
          const row = {
            responseId: Commons.EncryptAES(item.USER_ID),
            title: Commons.EncryptAES('작업허가서'),
            body: Commons.EncryptAES('작업허가서 승인이 완료되었습니다.'),
            gubun: Commons.EncryptAES('AL_002_003'),
            fcmToken: Commons.EncryptAES(''),
            requestId: userInfo.userid,
            route: Commons.EncryptAES(`/M_Permit`),
            responseData: Commons.EncryptAES(item.PM_CD),
          };
          saveData.push(row);
        });
      }

      const result = await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.FCM.fcm,
        saveData
      );

      const data = result.data;

      if (Array.isArray(data) && data.length > 0) {
        if (data[0].type === 'Error') {
          dispatch(showSnackbar({ message: data[0].message, type: 'error' }));
        } else if (data === 'Fail') {
          dispatch(
            showSnackbar({
              message: '사용자가 어플에 로그인하지 않았습니다.',
              type: 'warning',
            })
          );
        } else {
          dispatch(
            showSnackbar({
              message: '알림이 전송되었습니다.',
              type: 'success',
            })
          );
        }
      }
    } catch (error) {
      console.error('🔥 Exception in SignRequest:', error);

      dispatch(
        showSnackbar({
          message: '알림 전송 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };
  //#endregion

  return (
    <div className="wrap">
      <SAF_BASIC_02
        gridId1={'grid1'}
        userInfo={userInfo}
        rowData1={rowData1}
        gridRef1={gridRef1}
        gridRef4={gridRef4}
        setRowData1={setRowData1}
        selectedRowData1={selectedRowData1}
        setSelectedRowData1={setSelectedRowData1}
        changedRowData1={changedRowData1}
        setChangedRowData1={setChangedRowData1}
        selectedRowNosRef1={selectedRowNosRef1}
        deletedRowData1={deletedRowData1}
        lastCheckedIndexRef1={lastCheckedIndexRef1}
        searchRef={props.searchRef}
        dispatch={dispatch}
        showSnackbar={showSnackbar}
        modalToggle1={modalToggle1}
        setModalToggle1={setModalToggle1}
        modalToggle2={modalToggle2}
        setModalToggle2={setModalToggle2}
        modalToggle3={modalToggle3}
        setModalToggle3={setModalToggle3}
        modalRowData3={modalRowData3}
        setModalRowData3={setModalRowData3}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        signInputs={signInputs}
        setSignInputs={setSignInputs}
        signatureCanvas={signatureCanvas}
        validateSelection={props.validateSelection}
        SearchLogic1={SearchLogic1}
        SearchReturn={SearchReturn}
        SaveLogic1={SaveLogic1}
        RejectLogic={RejectLogic}
        ApproveLogic={ApproveLogic}
        SaveLogic3={SaveLogic3}
        NewYnErrorMsg={NewYnErrorMsg}
        handleRowSelection1={handleRowSelection1}
        RestoreRow1={deleteHandler1.RestoreRow}
        MarkedForDeletion1={deleteHandler1.MarkedForDeletion}
        DeleteNewRow1={deleteHandler1.DeleteNewRow}
        PopupClickEvent1={PopupClickEvent1}
        PopupClickEvent2={PopupClickEvent2}
        PopupClickEvent3={PopupClickEvent3}
      />
    </div>
  );
}

export default SAF_BASIC_02F;
